import React, { useState, useEffect } from 'react';
import SearchableSelect from './SearchableSelect';
import AddButton from './AddButton';
import { AddClientModal, AddSupplierModal, AddExporterModal } from './modals';
import weighingService from '@/services/weighing';

interface ClientSupplierSectionProps {
  formData: {
    client: string;
    fournisseur: string;
    exportateur: string;
  };
  onInputChange: (field: string, value: any) => void;
}

const ClientSupplierSection: React.FC<ClientSupplierSectionProps> = ({ formData, onInputChange }) => {
  // États pour les modales
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [isExporterModalOpen, setIsExporterModalOpen] = useState(false);

  // États pour les données
  const [clients, setClients] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [exporters, setExporters] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les clients
      const clientsResponse = await weighingService.getClients();
      if (clientsResponse && clientsResponse.results) {
        setClients(clientsResponse.results || []);
      }

      // Charger les fournisseurs
      const suppliersResponse = await weighingService.getFournisseurs();
      if (suppliersResponse && suppliersResponse.results) {
        setSuppliers(suppliersResponse.results || []);
      }

      // Charger les exportateurs
      const exportersResponse = await weighingService.getExportateurs();
      if (exportersResponse && exportersResponse.results) {
        setExporters(exportersResponse.results || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Convertir les données en format pour SearchableSelect
  const clientOptions = clients.map(c => ({
    value: c.id.toString(),
    label: `${c.nom} - ${c.type}`
  }));

  const supplierOptions = suppliers.map(s => ({
    value: s.id.toString(),
    label: `${s.nom} - ${s.cooperative}`
  }));

  const exporterOptions = exporters.map(e => ({
    value: e.id.toString(),
    label: `${e.nom} - ${e.type || 'N/A'}`
  }));

  // Gestionnaires pour les modales
  const handleAddClient = async (clientData: any) => {
    try {
      await weighingService.createClient(clientData);
      await loadData();
      setIsClientModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du client:', error);
    }
  };

  const handleAddSupplier = async (supplierData: any) => {
    try {
      await weighingService.createFournisseur(supplierData);
      await loadData();
      setIsSupplierModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du fournisseur:', error);
    }
  };

  const handleAddExporter = async (exporterData: any) => {
    try {
      await weighingService.createExportateur(exporterData);
      await loadData();
      setIsExporterModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'exportateur:', error);
    }
  };


  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
          <span className="ml-2 text-sm text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <SearchableSelect
          value={formData.client}
          onChange={(value) => onInputChange('client', value)}
          options={clientOptions}
          placeholder="Sélectionner un client"
          className="flex-1"
        />
        <AddButton 
          tooltip="Ajouter un nouveau client" 
          onClick={() => setIsClientModalOpen(true)}
        />
      </div>

      <div className="flex items-center space-x-2">
        <SearchableSelect
          value={formData.fournisseur}
          onChange={(value) => onInputChange('fournisseur', value)}
          options={supplierOptions}
          placeholder="Sélectionner un fournisseur"
          className="flex-1"
        />
        <AddButton 
          tooltip="Ajouter un nouveau fournisseur" 
          onClick={() => setIsSupplierModalOpen(true)}
        />
      </div>

      <div className="flex items-center space-x-2">
        <SearchableSelect
          value={formData.exportateur}
          onChange={(value) => onInputChange('exportateur', value)}
          options={exporterOptions}
          placeholder="Sélectionner un exportateur"
          className="flex-1"
        />
        <AddButton 
          tooltip="Ajouter un nouvel exportateur" 
          onClick={() => setIsExporterModalOpen(true)}
        />
      </div>


      {/* Modales */}
      <AddClientModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        onSave={handleAddClient}
      />

      <AddSupplierModal
        isOpen={isSupplierModalOpen}
        onClose={() => setIsSupplierModalOpen(false)}
        onSave={handleAddSupplier}
      />

      <AddExporterModal
        isOpen={isExporterModalOpen}
        onClose={() => setIsExporterModalOpen(false)}
        onSave={handleAddExporter}
      />
    </div>
  );
};

export default ClientSupplierSection;